import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { EASE_LUSION } from './easing';


/**
 * The behaviour behind CopyPill, on its own, for the links in the menu
 * overlay: they are plain text rather than pills, but they are the same
 * mailto:/tel: coin flip and need the same guarantee that something happens.
 */
export const useCopy = (value: string, href: string, onCopied?: () => void) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // A modified or middle click is the visitor asking the browser to do its
    // own thing with the link. Leave those alone entirely.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    // Copy first, hand off second — the order is the whole trick.
    //
    // Letting the anchor navigate on its own looked right and failed on a real
    // browser: the mailto: handoff starts immediately, Chrome raises its
    // "Open Mail?" prompt, the document loses focus, and navigator.clipboard
    // rejects with "Document is not focused". Nothing reached the clipboard,
    // and since a failed copy stays silent, the link did nothing at all.
    // Headless browsers never raise that prompt, which is why this passed.
    e.preventDefault();
    onCopied?.();

    void writeClipboard(value).then((ok) => {
      // A failed copy stays silent rather than claiming success — a lying
      // confirmation is worse than none.
      if (ok) {
        setCopied(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 1900);
      }
      // now the mail or phone app, for a visitor who has one. The click's
      // activation is still live, so this is not treated as a popup.
      window.location.href = href;
    });
  };

  return { copied, onClick };
};

/**
 * `navigator.clipboard` needs a secure context and is missing on a few older
 * mobile browsers. The offscreen-textarea trick is the only thing left there,
 * and it has to stay inside the click's user gesture, so no awaiting first.
 */
export const writeClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* denied or insecure context — fall through to the old way */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

/** ArrowMark's twin: same 12x12 box, same 1em sizing and currentColor stroke,
 *  so it drops into the pills without changing their metrics. It draws itself
 *  rather than appearing, which is the one flourish this interaction gets. */
export const CheckMark: React.FC<{ drawn: boolean; className?: string }> = ({ drawn, className = '' }) => (
  <svg
    viewBox="0 0 12 12"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    className={`shrink-0 ${className}`}
  >
    <motion.path
      d="M2.6 6.2 4.9 8.6 9.4 3.6"
      initial={false}
      animate={{ pathLength: drawn ? 1 : 0 }}
      transition={{ duration: 0.4, ease: EASE_LUSION, delay: drawn ? 0.12 : 0 }}
    />
  </svg>
);

/**
 * The site's masked roll (see RevealText), applied to a label that swaps for a
 * moment. Both lines share one grid cell, so the pill is sized by the wider of
 * the two and never changes width mid-animation — a pill that resized would
 * drag the magnetic wrapper and the row beside it around with it.
 *
 * Only the real value is in the accessibility tree; "Copied" is decoration,
 * and the announcement is made by the live region in CopyPill instead.
 */
export const RollSwap: React.FC<{ swapped: boolean; children: React.ReactNode }> = ({ swapped, children }) => (
  <span className="grid overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="col-start-1 row-start-1 block whitespace-nowrap will-change-transform"
      animate={{ y: swapped ? '-115%' : '0%' }}
      transition={{ duration: 0.5, ease: EASE_LUSION }}
    >
      {children}
    </motion.span>
    <motion.span
      aria-hidden="true"
      className="col-start-1 row-start-1 block whitespace-nowrap will-change-transform"
      animate={{ y: swapped ? '0%' : '115%' }}
      transition={{ duration: 0.5, ease: EASE_LUSION }}
    >
      Copied
    </motion.span>
  </span>
);

interface CopyPillProps {
  /** mailto: or tel: — kept, so a visitor with a mail client still gets one,
   *  and so the link is copyable and openable from the context menu */
  href: string;
  /** what actually lands on the clipboard */
  value: string;
  label: string;
  cursor: string;
  className: string;
  /** what the pill reads as when it has just been copied */
  copiedClassName: string;
  /** the leading glyph, if the pill has one; it swaps for the check */
  lead?: React.ReactNode;
  /** the trailing glyph, if the pill has one; likewise */
  trail?: React.ReactNode;
  onCopied?: () => void;
}

/**
 * The contact pills. `mailto:` and `tel:` are a coin flip — they open the
 * visitor's mail or phone app if one is registered and do nothing whatsoever
 * if not, which made the site's primary call to action feel broken. So the
 * click also puts the value on the clipboard and says so, and something
 * visible now happens either way.
 */
export const CopyPill: React.FC<CopyPillProps> = ({
  href,
  value,
  label,
  cursor,
  className,
  copiedClassName,
  lead,
  trail,
  onCopied,
}) => {
  const { copied, onClick } = useCopy(value, href, onCopied);

  const mark = (glyph: React.ReactNode) => (
    <span className="grid place-items-center">
      <motion.span
        className="col-start-1 row-start-1 flex"
        animate={{ opacity: copied ? 0 : 1, scale: copied ? 0.5 : 1 }}
        transition={{ duration: 0.3, ease: EASE_LUSION }}
      >
        {glyph}
      </motion.span>
      <motion.span
        className="col-start-1 row-start-1 flex"
        animate={{ opacity: copied ? 1 : 0 }}
        transition={{ duration: 0.25, ease: EASE_LUSION }}
      >
        <CheckMark drawn={copied} />
      </motion.span>
    </span>
  );

  return (
    <a
      href={href}
      onClick={onClick}
      data-cursor={cursor}
      aria-label={label}
      className={`${className} ${copied ? copiedClassName : ''}`}
    >
      {lead && mark(lead)}
      <RollSwap swapped={copied}>{value}</RollSwap>
      {trail && mark(trail)}
      {/* the visual swap is decorative; this is what a screen reader hears */}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? `${label} copied to clipboard` : ''}
      </span>
    </a>
  );
};
